import React, { useContext, useRef, useCallback, useEffect } from 'react'
import { rootStoreContext } from '@app/components/App'
import { observer } from 'mobx-react-lite'
import { Labels } from '@app/components/Labels'
import { Renderer } from '@app/views/Renderer'

export const Canvas = observer(
  ({
    width,
    height,
    qualifyingRect,
    lapTimesRect,
    pitStopRect,
  }: {
    width: number
    height: number
    qualifyingRect: ClientRect
    lapTimesRect: ClientRect
    pitStopRect: ClientRect
  }) => {
    const rootStore = useContext(rootStoreContext)
    const rendererRef = useRef(null)
    const svgRef = useRef(null)

    const canvasRef = useCallback(
      node => {
        if (node !== null) {
          rendererRef.current = new Renderer({
            rootStore,
            canvas: node,
          })
        }
      },
      [rootStore]
    )

    const move = useCallback(
      ({ clientX, clientY }: { clientX: number; clientY: number }) => {
        if (svgRef.current && rendererRef.current) {
          const svgRect = svgRef.current.getBoundingClientRect()
          const x = clientX - svgRect.left
          const y = clientY - svgRect.top
          rendererRef.current.mouseMove(x, y)
        }
      },
      []
    )

    const handleTouchEnd = useCallback(
      (e: TouchEvent) => {
        e.preventDefault()
        if (rendererRef.current) {
          move(e.changedTouches[0])
          rendererRef.current.click()
          rendererRef.current.mouseLeave()
        }
      },
      [move]
    )

    useEffect(() => {
      svgRef.current &&
        svgRef.current.addEventListener('touchend', handleTouchEnd)
    }, [handleTouchEnd])

    useEffect(() => {
      if (rendererRef.current) {
        rendererRef.current.resize({
          width,
          height,
          pixelRatio: window.devicePixelRatio,
          qualifyingRect,
          lapTimesRect,
          pitStopRect,
        })
      }
    })

    return (
      <>
        <svg
          ref={svgRef}
          className="absolute top-0 left-0 overflow-visible z-20 fill-current"
          style={{
            width: `${width}px`,
            height: `${height}px`,
          }}
          onMouseDown={e => e.preventDefault()}
          onMouseMove={e => move(e)}
          onClick={() => {
            rendererRef.current && rendererRef.current.click()
          }}
          onMouseLeave={() => {
            rendererRef.current && rendererRef.current.mouseLeave()
            rendererRef.current && rendererRef.current.clearHover()
          }}
        >
          {rendererRef.current && (
            <>
              <Labels renderer={rendererRef.current} />
            </>
          )}
        </svg>
        <canvas
          className="absolute top-0 left-0"
          style={{
            width: `${width}px`,
            height: `${height}px`,
          }}
          ref={canvasRef}
        />
      </>
    )
  }
)
